import * as d3 from 'd3';
import PauseCircleFilledOutlinedIcon from '@mui/icons-material/PauseCircleFilledOutlined';
import PlayCircleFilledWhiteOutlinedIcon from '@mui/icons-material/PlayCircleFilledWhiteOutlined';
import { Box, ButtonBase, CircularProgress } from '@mui/material';
import { ComponentType } from 'react';

import { AudioClip, AudioClipStatus } from '@/api/audioClips';
import { UserDisplayPreferences } from '@/api/userAccount';
import { MarginConfig } from '@/charts/types';
import { formatTimestamp } from '@/components/data/Timestamp';

export const AUDIO_CLIP_LANE_HEIGHT_PX = 40;
const LOZENGE_HEIGHT_PX = 28;
const LOZENGE_TOP_PX = (AUDIO_CLIP_LANE_HEIGHT_PX - LOZENGE_HEIGHT_PX) / 2;
const ICON_SIZE_PX = LOZENGE_HEIGHT_PX - 4;

/**
 * `loading` lasts from the `play()` call, and from each `waiting` event, until the `playing` event: the audio is
 * requested but no sound plays yet.
 */
export type ClipPlaybackPhase = 'loading' | 'playing';

/** The clip that the chart plays or loads, or `null` when no clip plays. */
export type ClipPlayback = { clipId: number; phase: ClipPlaybackPhase } | null;

export type AudioClipElementProps = {
    clip: AudioClip;
    leftPx: number;
    widthPx: number;
    timeRangeLabel: string;
    /** `null` when this clip neither plays nor loads. */
    playbackPhase: ClipPlaybackPhase | null;
    onToggle: () => void;
};

/** Position and shape that every clip element shares, so that clips of any status line up in the lane. */
const lozengeLayoutSx = (leftPx: number, widthPx: number) =>
    ({
        position: 'absolute',
        left: leftPx,
        top: LOZENGE_TOP_PX,
        width: widthPx,
        height: LOZENGE_HEIGHT_PX,
        borderRadius: LOZENGE_HEIGHT_PX / 2,
        boxSizing: 'border-box',
    }) as const;

const SucceededClipLozenge = ({ leftPx, widthPx, timeRangeLabel, playbackPhase, onToggle }: AudioClipElementProps) => {
    const isLoading = playbackPhase === 'loading';
    const Icon = playbackPhase ? PauseCircleFilledOutlinedIcon : PlayCircleFilledWhiteOutlinedIcon;

    return (
        <ButtonBase
            aria-label={`${playbackPhase ? 'Pause' : 'Play'} audio clip ${timeRangeLabel}`}
            aria-busy={isLoading}
            onClick={onToggle}
            sx={{
                ...lozengeLayoutSx(leftPx, widthPx),
                backgroundColor: 'primary.main',
                color: 'common.white',
                justifyContent: 'flex-start',
                pointerEvents: 'auto',
                '&:hover': { backgroundColor: 'primary.dark' },
            }}
        >
            {isLoading ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: ICON_SIZE_PX,
                        height: ICON_SIZE_PX,
                        ml: '2px',
                    }}
                >
                    <CircularProgress size={18} color="inherit" aria-label="Loading…" />
                </Box>
            ) : (
                <Icon sx={{ fontSize: ICON_SIZE_PX, ml: '2px' }} />
            )}
        </ButtonBase>
    );
};

/** The device still sends the clip, so there is no audio to play yet. */
const InProgressClipLozenge = ({ leftPx, widthPx, timeRangeLabel }: AudioClipElementProps) => (
    <Box
        role="img"
        aria-label={`Audio clip ${timeRangeLabel} in progress`}
        sx={{
            ...lozengeLayoutSx(leftPx, widthPx),
            backgroundColor: 'grey.300',
            border: '2px dashed',
            borderColor: 'grey.500',
        }}
    />
);

/** A status with no entry draws nothing. */
const CLIP_ELEMENT_BY_STATUS: Partial<Record<AudioClipStatus, ComponentType<AudioClipElementProps>>> = {
    succeeded: SucceededClipLozenge,
    in_progress: InProgressClipLozenge,
};

export type AudioClipLaneProps = {
    clips: AudioClip[];
    xTimeScale: d3.ScaleTime<number, number, never>;
    margin: MarginConfig;
    /** Height of the plot. The lane starts right below it. */
    height: number;
    timeZone: UserDisplayPreferences['timeZone'];
    clipPlayback: ClipPlayback;
    onToggleClip: (clip: AudioClip) => void;
};

/**
 * Draws one element per clip over the lane that `createContinuousBandChartTemplate` reserves below the plot.
 * Position it in the same box as the chart SVG, so that `margin` and `height` place it over the lane.
 */
export const AudioClipLane = ({
    clips,
    xTimeScale,
    margin,
    height,
    timeZone,
    clipPlayback,
    onToggleClip,
}: AudioClipLaneProps) => {
    const [, widthPx] = xTimeScale.range();
    const formatTime = (time: string) =>
        formatTimestamp(new Date(time), { unit: 'time', includeSeconds: true, timeZone });

    return (
        <Box
            data-testid="audio-clip-lane"
            sx={{
                position: 'absolute',
                top: margin.top + height,
                left: margin.left,
                width: widthPx,
                height: AUDIO_CLIP_LANE_HEIGHT_PX,
                // empty lane space passes pointer input on to the chart below it
                pointerEvents: 'none',
            }}
        >
            {clips.map((clip) => {
                const ClipElement = CLIP_ELEMENT_BY_STATUS[clip.status];
                if (!ClipElement) {
                    return null;
                }

                const leftPx = xTimeScale(new Date(clip.start_time));
                const rightPx = xTimeScale(new Date(clip.end_time));
                return (
                    <ClipElement
                        key={clip.id}
                        clip={clip}
                        leftPx={leftPx}
                        widthPx={Math.max(LOZENGE_HEIGHT_PX, rightPx - leftPx)}
                        timeRangeLabel={`${formatTime(clip.start_time)} to ${formatTime(clip.end_time)}`}
                        playbackPhase={clip.id === clipPlayback?.clipId ? clipPlayback.phase : null}
                        onToggle={() => onToggleClip(clip)}
                    />
                );
            })}
        </Box>
    );
};
